// Per-user surplus-energy balance — a single persisted running total (kJ).
//
// Each time a day is finished, that day's surplus is added to this balance. The
// surplus is measured against two reference points (see `daySurplusKj`): energy
// under `goal + bonus` banks a POSITIVE surplus; energy over the upper threshold
// `goal + max(over tolerance, bonus)` contributes a NEGATIVE surplus; anything
// in between contributes 0. The stored balance is always clamped non-negative:
// if adding a day's surplus would take it below zero it is reset to 0.
//
// Stored PRIVATELY (PK = the caller's Cognito Identity ID) as a single row under
// SK `surplus-energy#balance`, value in the `balanceKj` attribute (kJ, the app's
// storage unit — independent of the display unit).

import { getItem, updateItem } from './db.js';

const SURPLUS_PREFIX = 'surplus-energy#';
const BALANCE_ID = `${SURPLUS_PREFIX}balance`;

/**
 * A single day's surplus energy, measured piecewise against two reference points:
 *   • consumed < `goalPlusBonusKj`  →  positive surplus = goalPlusBonus − consumed
 *   • consumed > `upperKj`          →  negative surplus = upper − consumed
 *   • otherwise (in between)        →  0
 * where `upperKj` (goal + max(over tolerance, bonus)) is ≥ `goalPlusBonusKj`
 * (goal + bonus). This makes the band [goal+bonus, upper] a dead-zone worth 0 —
 * e.g. with no bonus, [goal, goal+over tolerance] banks nothing and costs
 * nothing. Pure.
 */
export function daySurplusKj(consumedKj, goalPlusBonusKj, upperKj) {
  const consumed = Number(consumedKj) || 0;
  const goalPlusBonus = Number(goalPlusBonusKj) || 0;
  const upper = Number(upperKj) || 0;
  if (consumed < goalPlusBonus) return goalPlusBonus - consumed;
  if (consumed > upper) return upper - consumed;
  return 0;
}

/** Clamp a balance to a non-negative, finite number of kJ. Pure. */
export function clampBalanceKj(kj) {
  const n = Number(kj);
  if (!Number.isFinite(n) || n < 0) return 0;
  return n;
}

/**
 * Simulate eating every planned day and finishing them in order: each day over
 * its upper threshold draws its overshoot from the banked `balanceKj`, first
 * day first, until the balance runs out. Only the EXISTING balance is drawn on —
 * a planned under-target day doesn't bank anything for a later one.
 *
 * `days` = `[{ date, consumedKj, upperKj }]` in the order they'd be finished.
 * Returns `{ byDate, usedKj, remainingKj }`: `byDate` maps each over-target
 * day's date → `{ overKj, coveredKj }` (overshoot, and the part the surplus
 * covers); `usedKj` + `remainingKj` = the (clamped) balance. Pure.
 */
export function allocateSurplus(days, balanceKj) {
  const balance = clampBalanceKj(balanceKj);
  let remainingKj = balance;
  const byDate = new Map();
  for (const { date, consumedKj, upperKj } of days) {
    const overKj = Math.max(0, (Number(consumedKj) || 0) - (Number(upperKj) || 0));
    if (overKj <= 0) continue;
    const coveredKj = Math.min(overKj, remainingKj);
    remainingKj -= coveredKj;
    byDate.set(date, { overKj, coveredKj });
  }
  return { byDate, usedKj: balance - remainingKj, remainingKj };
}

/** Read the current surplus-energy balance in kJ (0 when never set). */
export async function getSurplusKj(doc, userId) {
  const row = await getItem(doc, userId, BALANCE_ID);
  return clampBalanceKj(row?.balanceKj);
}

/** Write the surplus-energy balance (upsert), clamped non-negative. Returns the
 * stored value. */
export async function setSurplusKj(doc, userId, kj) {
  const balanceKj = clampBalanceKj(kj);
  await updateItem(doc, userId, BALANCE_ID, { balanceKj });
  return balanceKj;
}

/** Add a day's surplus (which may be negative) to the balance, clamping the
 * result non-negative, and persist it. Returns the new balance in kJ. */
export async function addSurplusKj(doc, userId, deltaKj) {
  const current = await getSurplusKj(doc, userId);
  return setSurplusKj(doc, userId, current + (Number(deltaKj) || 0));
}
